/**
 * When an invoice's payment landed, in the two pieces the "Tự khớp chuyển
 * khoản lúc {{time}} hôm nay" sentence needs (design §3).
 *
 * Translation-free on purpose: this lib decides the CLOCK question (which
 * timezone, is it today), i18n decides the SENTENCE. Returning a pre-composed
 * "09:41 hôm nay" would put Vietnamese inside a lib and force the English file
 * to re-split it.
 *
 * ── The timezone trap, which is real ──────────────────────────────────────
 * `Payment.transactionDate` is a REAL instant: SePay sends a zone-less VN
 * local string and apps/api/src/services/sepayIpnPayload.ts converts it with
 * `+07:00` before storing. Almost everything else the dashboard formats
 * (session `startAt`, invoice `dueDate`) is wall-clock-as-UTC and is read with
 * UTC getters — the opposite convention.
 *
 * Meanwhile `useServerNow()` returns the VN WALL CLOCK labelled Z (see its doc
 * comment), so "now" must be read with UTC getters.
 *
 * So: format `at` in Asia/Ho_Chi_Minh, and compare its VN calendar day against
 * `now`'s UTC calendar day. Read either one the other's way and "09:41 hôm
 * nay" reads "20/08" for the seven hours after midnight VN, every night.
 */
const VN_ZONE = 'Asia/Ho_Chi_Minh';

const VN_TIME = new Intl.DateTimeFormat('vi-VN', {
  timeZone: VN_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

// `formatToParts` with an explicit en-US numeric format rather than an
// `en-CA`/`sv-SE` "gives ISO order" trick, or a `vi-VN` day/month formatter:
// the parts are read by name, so both the ORDER and the SEPARATOR are stable
// regardless of what a given ICU build decides `vi-VN`'s defaults are. That
// is not a hypothetical — this runtime's ICU renders a `vi-VN`
// `{ day: '2-digit', month: '2-digit' }` formatter as "14-08", not "14/08";
// the slash in `dd/MM` below is composed by hand rather than trusted to it.
const VN_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: VN_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function vnParts(d: Date): { year: string; month: string; day: string } {
  const parts = VN_PARTS.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { year: get('year'), month: get('month'), day: get('day') };
}

function vnDayKey(d: Date): string {
  const { year, month, day } = vnParts(d);
  return `${year}-${month}-${day}`;
}

/** `dd/MM` in Vietnam — composed from named parts, not a locale formatter's
 *  separator (see the ICU note above). */
function vnDayMonth(d: Date): string {
  const { month, day } = vnParts(d);
  return `${day}/${month}`;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export interface MatchedAt {
  /** `HH:mm` in Vietnam. */
  time: string;
  /** `dd/MM` in Vietnam, or null when it happened on `now`'s day — the caller
   *  then uses the "… hôm nay" phrasing instead. */
  date: string | null;
}

/**
 * @param at  ISO instant from `InvoiceLastPayment.at`.
 * @param now From `useServerNow()` — the VN wall clock labelled Z, never
 *            `new Date()` (the dev clock can time-travel).
 * @returns   null for an unparseable/empty timestamp, so the caller renders
 *            nothing rather than "lúc Invalid Date".
 */
export function fmtMatchedAt(at: string | null | undefined, now: Date): MatchedAt | null {
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;

  const nowKey = `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}-${pad2(now.getUTCDate())}`;
  const sameDay = vnDayKey(d) === nowKey;

  return { time: VN_TIME.format(d), date: sameDay ? null : vnDayMonth(d) };
}
